import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { apiClient } from 'shared/apiClient';
import { ENTITLEMENTS_STALE_TIME } from 'shared/consts';
import { isSessionEndingError, redirectIfSessionExpired, type ErrorReporting } from 'shared/utils';

import { CAPABILITIES_FEATURE_TAG, CUSTOMER_CAPABILITIES_QUERY_KEY } from './consts';

/**
 * Query options for the customer's capabilities. Use with `ensureQueryData` / `fetchQuery` (route
 * guards) or `useQuery` (hooks). The cache holds the API response; every entry point below hands out
 * its `data` list, so consumers and the accessors work on `Capability[]`.
 */
export const customerCapabilitiesQuery = queryOptions({
  queryKey: CUSTOMER_CAPABILITIES_QUERY_KEY,
  queryFn: ({ signal }) => apiClient.capabilities.getCustomerCapabilities({ signal }),
  staleTime: ENTITLEMENTS_STALE_TIME,
  meta: {
    errorReporting: {
      feature: CAPABILITIES_FEATURE_TAG,
      errorMessage: 'Failed to fetch customer capabilities',
      method: 'apiClient.capabilities.getCustomerCapabilities'
    } satisfies ErrorReporting
  }
});

/*
 * Customer capabilities access — four entry points over one cache key; pick by call-site:
 *   • ensureCustomerCapabilities     — seed the cache only if missing, `null` on failure (root `_authenticated` guard, login).
 *   • revalidateCustomerCapabilities — refetch only when stale, last loaded list or `null` on failure (route guards right before a gate).
 *   • getCachedCustomerCapabilities  — read the cache without fetching (sync callers such as post-login navigation).
 *   • useCustomerCapabilities        — reactive access from components.
 */

/**
 * Ensures the customer capabilities are in the query client cache, fetching only if missing, and
 * resolves to the capabilities list. Resolves to `null` when the read fails so a capabilities outage
 * never blocks login; the gates treat a missing snapshot as "not allowed", and the failure is
 * reported through the query's `meta`.
 */
export const ensureCustomerCapabilities = (queryClient: QueryClient) =>
  queryClient
    .ensureQueryData(customerCapabilitiesQuery)
    .then(({ data }) => data)
    .catch(() => null);

/** Reads the cached capabilities list without fetching; `undefined` when nothing is cached yet. */
export const getCachedCustomerCapabilities = (queryClient: QueryClient) =>
  queryClient.getQueryData(customerCapabilitiesQuery.queryKey)?.data;

interface RevalidateCustomerCapabilitiesParams {
  /** Router-context query client */
  queryClient: QueryClient;
  /** Attempted internal href (`pathname + search + hash`), kept for the return after a session-expired login */
  attemptedHref: string;
}

/**
 * Returns the cached capabilities list while it is fresh (younger than
 * {@link ENTITLEMENTS_STALE_TIME}) and refetches otherwise. When the refetch fails it keeps the last
 * loaded list, as `useCustomerCapabilities` does, and resolves to `null` when no read has succeeded,
 * so a gate fails closed; the failure is reported through the query's `meta`. An ended session
 * redirects to the session-expired login instead.
 */
export const revalidateCustomerCapabilities = ({ queryClient, attemptedHref }: RevalidateCustomerCapabilitiesParams) =>
  queryClient
    .fetchQuery(customerCapabilitiesQuery)
    .then(({ data }) => data)
    .catch((error: unknown) => {
      if (isSessionEndingError(error)) {
        return redirectIfSessionExpired(error, attemptedHref);
      }

      return getCachedCustomerCapabilities(queryClient) ?? null;
    });
